import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Loader } from '@els/els-react--loader';
import Button from '@Components/Button';
import store from '@Store';
import uuid from 'uuid/v4';
import { open as openStructureDialog } from '@Containers/StructureDialog/StructureDialogAction';
import useStructureSrc from '@Containers/StructureDialog/useStructureSrc';
import ImageBase64 from '@Components/ImageBase64';
import Tooltip, { TooltipConstants } from '@Components/Tooltip';
import HoverTooltip from '@Components/HoverTooltip';
import { showCopiedToastMessage } from '@Containers/ToastMessages/CopiedToastMessage/CopiedToastMessageAction';

import './Substructure.theme.scss';

function Substructure({ smiles, removeSmiles, displayRemoveIcon }) {
  const structureSrc = useStructureSrc(smiles.value);

  const zoomSubstructure = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      store.dispatch(openStructureDialog(uuid(), smiles.value));
    },
    [smiles]
  );

  const copySmiles = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      navigator.clipboard?.writeText(smiles.value);
      store.dispatch(showCopiedToastMessage('The SMILES string of the structure has been copied to the clipboard.'));
    },
    [smiles]
  );

  return (
    <div className="substructure">
      <ImageBase64 src={structureSrc}>
        {({ src }) =>
          src ? (
            <>
              <div className="substructure__smiles-image-wrapper">
                <img src={src} alt={smiles.value} className="substructure__smiles-image" />
              </div>
              <div className="substructure__smiles-icons">
                <HoverTooltip
                  theme="simple"
                  className="substructure__smiles-icon-wrapper"
                  id={TooltipConstants.ids.RetrosynthesisStructureIcon}
                  tooltip="Zoom">
                  <Button
                    buttonConfig={{
                      className: 'substructure__smiles-icon zoom',
                      type: 'link',
                      size: 'sm',
                      e2eName: 'copy',
                      iconOnly: true,
                      ariaLabel: 'Zoom',
                    }}
                    iconConfig={{
                      name: 'zoom',
                      noBorder: true,
                    }}
                    onClick={zoomSubstructure}
                  />
                </HoverTooltip>
                <HoverTooltip
                  theme="simple"
                  className="substructure__smiles-icon-wrapper"
                  id={TooltipConstants.ids.RetrosynthesisStructureIcon}
                  tooltip="Copy SMILES">
                  <Button
                    buttonConfig={{
                      className: 'substructure__smiles-icon',
                      type: 'link',
                      size: 'sm',
                      e2eName: 'copy',
                      iconOnly: true,
                      ariaLabel: 'Copy SMILES',
                    }}
                    iconConfig={{
                      name: 'duplicate-document',
                      noBorder: true,
                    }}
                    onClick={copySmiles}
                  />
                </HoverTooltip>
                {displayRemoveIcon && (
                  <HoverTooltip
                    className="substructure__smiles-icon-wrapper"
                    theme="simple"
                    id={TooltipConstants.ids.RetrosynthesisStructureIcon}
                    tooltip="Delete structure">
                    <Button
                      buttonConfig={{
                        className: 'substructure__smiles-icon delete',
                        type: 'link',
                        size: 'sm',
                        e2eName: 'copy',
                        iconOnly: true,
                        ariaLabel: 'Delete structure',
                      }}
                      iconConfig={{
                        name: 'delete-document',
                        noBorder: true,
                      }}
                      onClick={() => {
                        removeSmiles(smiles);
                        Tooltip.hide();
                      }}
                    />
                  </HoverTooltip>
                )}
              </div>
            </>
          ) : (
            <div className="substructure__smiles-image-wrapper">
              <Loader isSmall isBlocking={false} className="substructure__image-loader" />
            </div>
          )
        }
      </ImageBase64>
    </div>
  );
}
Substructure.defaultProps = {
  displayRemoveIcon: true,
};
Substructure.propTypes = {
  smiles: PropTypes.object.isRequired,
  removeSmiles: PropTypes.func,
  displayRemoveIcon: PropTypes.bool,
};

export default Substructure;
